import { TZDate } from '@date-fns/tz';
import { MeetingNote, PmStatus, ProviderType } from '../../api/types';

// Format a Date object into a string like "2025-07-01",
// removing the time part and keeping the local timezone,
// without converting to UTC like "toISOString" does.
export const formatDateToDayOnly = (date: TZDate) => {
  const year = date.getFullYear();
  // months are 0-based
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const computeMonthEndDate = (
  monthStartDate: TZDate,
  timezone: string
) => {
  // create a date at the 0th day of the next month,
  // which is effectively the last day of the current month
  return new TZDate(
    monthStartDate.getFullYear(),
    monthStartDate.getMonth() + 1,
    0,
    timezone
  );
};

/**
 * Formats an ISO date-time string to a 12-hour time (e.g. "8:15 PM"),
 * displayed in the given target timezone.
 *
 * @param isoString - A valid ISO 8601 date-time string (e.g. "2025-07-10T20:15:00+02:00")
 * @param timezone - IANA timezone name (e.g. "Europe/Rome", "America/New_York")
 * @returns A formatted time string in 12-hour format with AM/PM
 */
export function formatTimeFromISOStringWithTZ(
  isoString: string,
  timezone: string
): string {
  const date = new TZDate(isoString, timezone);

  const hours = date.getHours();
  const minutes = date.getMinutes();

  // Convert to 12-hour format
  const isPM = hours >= 12;
  // Convert "0" to "12" for 12 AM
  const hours12 = hours % 12 || 12;
  // Ensure minutes are always two digits (e.g. "5" → "05") for consistent formatting
  const paddedMinutes = minutes.toString().padStart(2, '0');

  return `${hours12}:${paddedMinutes} ${isPM ? 'PM' : 'AM'}`;
}

/**
 * Returns the short timezone abbreviation (e.g. "EST", "PDT", "CEST")
 * for a given IANA timezone name and optional date.
 *
 * @param timezone - A IANA timezone identifier (e.g. "America/New_York", "Europe/Rome").
 * @param date - (Optional) A specific Date to account for daylight saving time.
 *               Defaults to the current date/time.
 * @returns A short timezone abbreviation as a string (e.g. "EDT", "CET").
 *
 * Note: The result reflects daylight saving time if applicable on the given date.
 */
export const computeTimezoneAbbreviation = (
  timezone: string,
  date: Date = new Date()
): string => {
  return (
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value || ''
  );
};

// returns future meetings that still need to happen
export const computeNextMeetings = <
  T extends { meeting_date: string; pm_status?: PmStatus }
>(
  meetings: T[]
) => {
  const now = new Date();

  return meetings
    .filter((meeting: T) => {
      const date = new Date(meeting.meeting_date);

      // include next future meetings
      return date > now && !meeting.pm_status;
    })
    .sort(
      (a: T, b: T) =>
        new Date(a.meeting_date).getTime() - new Date(b.meeting_date).getTime()
    );
};

export const hasFutureMeetingWithProviderType = (
  meetingNotes: MeetingNote[] | undefined,
  providerType: ProviderType
): boolean => {
  if (!meetingNotes?.length) return false;

  const now = new Date();

  return meetingNotes.some(
    (meeting) =>
      // filter out cancelled meetings
      // (that would have 'Cancelled' or 'Late Cancellation')
      !meeting.pm_status &&
      meeting.provider?.type === providerType &&
      meeting.meeting_date != null &&
      new Date(meeting.meeting_date) > now
  );
};

/**
 * Checks whether a meeting (given by its ISO 8601 date string) has already started.
 *
 * - Converts the ISO string (with timezone/offset) into a JavaScript Date.
 * - Compares its timestamp against the current time (`Date.now()`).
 * - Returns `true` if the meeting start time is in the past or exactly now,
 *   otherwise `false`.
 */
export const hasMeetingStarted = (isoDate: string | undefined): boolean => {
  if (!isoDate) return false; // no date = not started

  const timestamp = new Date(isoDate).getTime();

  // if invalid date string, Date.getTime() will be NaN
  if (isNaN(timestamp)) return false;

  return timestamp <= Date.now();
};
